import { useState } from 'react';

/**
 * Bulk paste import for racetime variant starts.
 * Pipe-delimited format: class | flag | time
 *
 * Example input:
 *   Beneteau 31.7 | No. 9 | 14:10
 *   Cruisers 2 | No. 2 | 14:15
 *   Cruisers 4 & 5 | No. 4 & No. 5 | 14:20
 */
export default function StartsPasteImport({ availClasses, existingClasses = [], onImport, onClose }) {
  const [rawText, setRawText] = useState('');
  const [parsedResults, setParsedResults] = useState(null);

  function resolveClassIds(classText) {
    // Split on " & " to handle multi-class entries like "Cruisers 4 & 5"
    const names = classText.split(/\s*&\s*/);
    const resolved = [];
    const unresolved = [];

    for (const name of names) {
      const trimmed = name.trim();
      if (!trimmed) continue;

      // Try exact match on ID first
      const byId = availClasses.find((c) => c.id === trimmed);
      if (byId) { resolved.push(byId.id); continue; }

      // Try exact match on name (case-insensitive)
      const byName = availClasses.find(
        (c) => c.name && c.name.toLowerCase() === trimmed.toLowerCase(),
      );
      if (byName) { resolved.push(byName.id); continue; }

      // Try substring/prefix match on name
      const byPrefix = availClasses.find(
        (c) => c.name && c.name.toLowerCase().startsWith(trimmed.toLowerCase()),
      );
      if (byPrefix) { resolved.push(byPrefix.id); continue; }

      // Try contains match
      const byContains = availClasses.find(
        (c) => c.name && c.name.toLowerCase().includes(trimmed.toLowerCase()),
      );
      if (byContains) { resolved.push(byContains.id); continue; }

      unresolved.push(trimmed);
    }
    return { resolved, unresolved };
  }

  function normalizeFlag(flag) {
    // Normalize "No.9" → "No. 9", "No 9" → "No. 9", leave others (Q, W, K, B) as-is
    return flag.trim().replace(/^No\.?\s*(\d+)$/i, 'No. $1');
  }

  function parseFlags(flagText) {
    // Split on " & " for multi-flag entries like "No. 4 & No. 5"
    return flagText.split(/\s*&\s*/).map(normalizeFlag).filter(Boolean);
  }

  function parseLine(line) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      return { line: trimmed, status: 'skip', start: null, message: 'Comment or empty' };
    }

    // Pipe-delimited only: class | flag | time
    const parts = trimmed.split('|').map((p) => p.trim());
    let time = null;
    let flagStr = '';
    let classStr = '';

    if (parts.length >= 3) {
      classStr = parts[0];
      flagStr = parts[1].replace(/^Flag:\s*/i, '');
      time = parts[parts.length - 1];
    } else if (parts.length === 2) {
      // class | time (no flag column)
      classStr = parts[0];
      time = parts[1];
    } else {
      return { line: trimmed, status: 'error', start: null, message: 'Expected pipe-delimited format: class | flag | time' };
    }

    // Validate time format
    if (!/^\d{1,2}:\d{2}$/.test(time)) {
      return { line: trimmed, status: 'error', start: null, message: `Invalid time format: "${time}" — expected HH:MM (e.g. 14:10)` };
    }

    // Pad time to HH:MM
    const [h, m] = time.split(':');
    time = `${h.padStart(2, '0')}:${m}`;

    // Parse flags
    const flags = flagStr ? parseFlags(flagStr) : [];

    // Resolve class names to IDs
    const { resolved: classIds, unresolved } = resolveClassIds(classStr);

    if (classIds.length === 0 && unresolved.length > 0) {
      const availNames = availClasses.map((c) => c.name).filter(Boolean);
      return {
        line: trimmed,
        status: 'warn',
        start: { time, flags, classes: [], classText: classStr },
        message: `Class not found: ${unresolved.join(', ')}. Available: ${availNames.join(', ')}`,
      };
    }

    // Check if any resolved classes are already in starts
    const alreadyAdded = classIds.filter((id) => existingClasses.includes(id));

    const messages = [];
    if (unresolved.length > 0) {
      messages.push(`Not found: ${unresolved.join(', ')}`);
    }
    if (alreadyAdded.length > 0) {
      const names = alreadyAdded.map((id) => {
        const cls = availClasses.find((c) => c.id === id);
        return cls?.name || id;
      });
      messages.push(`Already added: ${names.join(', ')}`);
    }

    const status = unresolved.length > 0 || alreadyAdded.length > 0 ? 'warn' : 'ok';
    const message = messages.length > 0 ? messages.join('. ') : null;

    return {
      line: trimmed,
      status,
      start: { time, flags, classes: classIds, classText: classStr },
      message,
    };
  }

  function handlePreview() {
    if (!rawText.trim()) return;
    const lines = rawText.trim().split('\n');
    setParsedResults(lines.map(parseLine));
  }

  function handleImport() {
    if (!parsedResults) return;
    const starts = parsedResults
      .filter((r) => (r.status === 'ok' || r.status === 'warn') && r.start)
      .map((r) => ({
        time: r.start.time,
        flags: r.start.flags,
        classes: r.start.classes.length > 0 ? r.start.classes : [r.start.classText],
      }));
    if (starts.length > 0) onImport(starts);
  }

  const okCount = parsedResults?.filter((r) => r.status === 'ok').length ?? 0;
  const warnCount = parsedResults?.filter((r) => r.status === 'warn').length ?? 0;
  const errorCount = parsedResults?.filter((r) => r.status === 'error').length ?? 0;
  const importCount = okCount + warnCount;

  return (
    <div className="paste-import">
      <div className="paste-import-header">
        <h3>Paste Import Starts</h3>
        {onClose && (
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        )}
      </div>

      {availClasses.length > 0 && (
        <details className="paste-import-classes">
          <summary>Available classes ({availClasses.length})</summary>
          <ul className="class-name-list">
            {availClasses.map((c) => (
              <li key={c.id}>{c.name || c.id}</li>
            ))}
          </ul>
        </details>
      )}

      <label className="form-field">
        <span className="form-label">
          Paste start times
          <span className="form-hint">
            Format: class | flag | time (one per line)
          </span>
        </span>
        <textarea
          value={rawText}
          onChange={(e) => { setRawText(e.target.value); setParsedResults(null); }}
          placeholder={`Beneteau 31.7 | No. 9 | 14:10\nCruisers 2 | No. 2 | 14:15\nCruisers 4 & 5 | No. 4 & No. 5 | 14:20`}
          rows={8}
          spellCheck={false}
          className="mono-input"
        />
      </label>

      <div className="paste-import-actions">
        <button
          className="btn btn-primary"
          onClick={handlePreview}
          disabled={!rawText.trim()}
        >
          Preview
        </button>
      </div>

      {parsedResults && (
        <div className="paste-import-results">
          <p className="paste-import-summary">
            {okCount} parsed{warnCount > 0 ? `, ${warnCount} warnings` : ''}, {errorCount} failed,{' '}
            {parsedResults.filter((r) => r.status === 'skip').length} skipped
          </p>

          <table className="paste-results-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Time</th>
                <th>Flag(s)</th>
                <th>Class(es)</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {parsedResults
                .filter((r) => r.status !== 'skip')
                .map((r, i) => (
                  <tr key={i} className={`paste-result-${r.status}`}>
                    <td>
                      <span className={`status-dot ${r.status}`}>
                        {r.status === 'ok' ? 'OK' : r.status === 'warn' ? '!' : 'ERR'}
                      </span>
                    </td>
                    <td className="mono-text">{r.start?.time || '—'}</td>
                    <td>{r.start?.flags?.join(', ') || '—'}</td>
                    <td>
                      {r.start?.classes?.length
                        ? r.start.classes.map((id) => {
                            const cls = availClasses.find((c) => c.id === id);
                            return cls ? cls.name : id;
                          }).join(', ')
                        : r.start?.classText || '—'}
                    </td>
                    <td>
                      {r.message && <span className={r.status === 'error' ? 'error-text' : 'warn-text'}>{r.message}</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={importCount === 0}
          >
            Import {importCount} start{importCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}
